import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type HTMLAttributes,
  type Ref,
} from "react";

import { useVisualEffects } from "./useVisualEffects";
import { LiquidGlassFilter } from "./LiquidGlassFilter";
import { createGlassMap, readGlassRect, supportsGlassRefraction, type GlassMap } from "./liquidGlassOptics";
import { LiquidGlassGroupContext, subscribeWithoutGroup } from "./liquidGlassGroupContext";
import "./VisualEffects.css";

export type LiquidGlassProps = HTMLAttributes<HTMLElement> & {
  as?: "div" | "header" | "aside" | "nav";
  clear?: boolean;
  active?: boolean;
  /** Adapt the material tint to actual backdrop luminance. */
  adaptation?: "auto" | "none";
  ref?: Ref<HTMLElement>;
};

export function LiquidGlass({
  as: Component = "div",
  children,
  className = "",
  clear = false,
  active = false,
  adaptation = "auto",
  style,
  ref,
  ...props
}: LiquidGlassProps) {
  const { level, reducedMotion, reducedTransparency } = useVisualEffects();
  const root = useRef<HTMLElement>(null);
  const group = useContext(LiquidGlassGroupContext);
  const grouped = useSyncExternalStore(group?.subscribe ?? subscribeWithoutGroup,
    () => group?.contains(root.current) ?? false, () => false);
  useEffect(() => {
    const element = root.current;
    return element && group ? group.register(element) : undefined;
  }, [Component, group]);
  // Radix composes this ref for focus restoration, positioning and dismissal.
  const attach = useCallback(
    (element: HTMLElement | null) => {
      root.current = element;
      if (typeof ref === "function") return ref(element);
      if (ref) ref.current = element;
    },
    [ref],
  );
  const filterId = `ark-liquid-${useId().replace(/:/g, "")}`;
  const [map, setMap] = useState<GlassMap>();
  const [chromium] = useState(supportsGlassRefraction);
  const refract = level === "rich" && !reducedTransparency && chromium && !grouped;

  useEffect(() => {
    const element = root.current;
    if (!refract || !element || typeof ResizeObserver === "undefined") return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = readGlassRect(element);
        const { width, height } = rect;
        if (width < 1 || height < 1) return;
        const next = createGlassMap(width, height, [rect]);
        if (next)
          setMap((previous) =>
            previous?.source === next.source &&
            previous.width === width &&
            previous.height === height
              ? previous
              : next,
          );
      });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [
    refract,
    Component,
    className,
    style?.borderRadius,
    style?.borderTopLeftRadius,
    style?.borderTopRightRadius,
    style?.borderBottomRightRadius,
    style?.borderBottomLeftRadius,
  ]);

  useEffect(() => {
    const element = root.current;
    if (
      !element ||
      grouped ||
      reducedMotion ||
      reducedTransparency ||
      (level !== "rich" && level !== "standard")
    )
      return;
    let frame = 0;
    const move = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      cancelAnimationFrame(frame);
      const { left, top } = element.getBoundingClientRect();
      const x = event.clientX - left;
      const y = event.clientY - top;
      frame = requestAnimationFrame(() => {
        element.style.setProperty("--ark-pointer-x", `${x}px`);
        element.style.setProperty("--ark-pointer-y", `${y}px`);
      });
    };
    element.addEventListener("pointermove", move, { passive: true });
    const leave = () => {
      cancelAnimationFrame(frame);
      element.style.removeProperty("--ark-pointer-x");
      element.style.removeProperty("--ark-pointer-y");
    };
    element.addEventListener("pointerleave", leave);
    return () => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", leave);
      leave();
    };
  }, [Component, grouped, level, reducedMotion, reducedTransparency]);

  return (
    <Component
      {...props}
      className={`ark-glass ark-liquid ${clear ? "ark-glass-clear" : ""} ${className}`}
      data-active={active || undefined}
      data-adaptation={adaptation}
      data-adaptation-fallback={level === "rich" && !reducedTransparency &&
        !grouped && adaptation === "auto" && !(refract && map) ? "true" : undefined}
      data-glass-grouped={grouped ? "true" : undefined}
      data-refraction={refract && Boolean(map) ? "true" : undefined}
      ref={attach}
      style={
        { ...style, "--ark-refraction": `url("#${filterId}")` } as CSSProperties
      }
    >
      {refract && map ? (
        <LiquidGlassFilter adaptive={adaptation === "auto"} id={filterId} map={map} />
      ) : null}
      {children}
    </Component>
  );
}
